<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center py-3">
      <span class="text-subtitle-1 font-weight-bold">
        お預かり金
      </span>
      <v-btn
        icon
        small
        @click="$emit('close')"
      >
        <v-icon small>
          {{ icons.mdiClose }}
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-divider />

    <v-card-text class="pt-4 pb-2">
      <!-- 預かり金合計 -->
      <div
        v-if="totalAmount > 0"
        class="mb-3"
      >
        <span class="text-subtitle-1 font-weight-bold">
          合計: ¥{{ totalAmount.toLocaleString() }}
        </span>
      </div>

      <!-- 入力フォーム -->
      <div class="d-flex align-center mb-3">
        <v-text-field
          v-model.number="inputAmount"
          type="number"
          label="金額"
          dense
          outlined
          hide-details
          class="mr-2"
          style="max-width: 160px"
        />
        <v-btn-toggle
          v-model="inputPaymentMethod"
          mandatory
          dense
          class="mr-2"
        >
          <v-btn
            value="cash"
            small
          >
            現金
          </v-btn>
          <v-btn
            value="card"
            small
          >
            カード
          </v-btn>
        </v-btn-toggle>
        <v-btn
          color="primary"
          small
          :loading="isSubmitting"
          :disabled="!inputAmount || inputAmount <= 0"
          @click="createDeposit"
        >
          登録
        </v-btn>
      </div>

      <!-- 預かり金一覧 -->
      <v-simple-table
        v-if="deposits.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th>種別</th>
              <th class="text-right">
                金額
              </th>
              <th>登録者</th>
              <th>日時</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="deposit in deposits"
              :key="deposit.id"
            >
              <td>
                <v-chip
                  x-small
                  :color="deposit.paymentMethod === 'cash' ? 'success' : 'info'"
                  dark
                >
                  {{ deposit.paymentMethod === 'cash' ? '現金' : 'カード' }}
                </v-chip>
              </td>
              <td class="text-right">
                ¥{{ deposit.amount.toLocaleString() }}
              </td>
              <td>{{ deposit.user ? deposit.user.name : '-' }}</td>
              <td>{{ formatTime(deposit.createdAt) }}</td>
              <td>
                <v-btn
                  icon
                  x-small
                  color="error"
                  :loading="cancellingId === deposit.id"
                  @click="confirmCancel(deposit)"
                >
                  <v-icon x-small>
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <!-- 取消確認ダイアログ -->
    <v-dialog
      v-model="showCancelDialog"
      max-width="320"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          預かり金の取消
        </v-card-title>
        <v-card-text>
          ¥{{ cancelTarget ? cancelTarget.amount.toLocaleString() : '' }}（{{ cancelTarget && cancelTarget.paymentMethod === 'cash' ? '現金' : 'カード' }}）を取消しますか？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showCancelDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            @click="cancelDeposit"
          >
            取消
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import { mdiClose } from '@mdi/js'
import DepositApi from '@/api/waiter/Deposit'
import TableApi from '@/api/waiter/Table'

export default {
  props: {
    tableId: {
      type: [Number, String],
      required: true,
    },
    initialDeposits: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const deposits = ref([...props.initialDeposits])
    const inputAmount = ref(null)
    const inputPaymentMethod = ref('cash')
    const isSubmitting = ref(false)
    const cancellingId = ref(null)
    const showCancelDialog = ref(false)
    const cancelTarget = ref(null)

    const totalAmount = computed(() => {
      return deposits.value.reduce((sum, d) => sum + d.amount, 0)
    })

    const formatTime = dateStr => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)

      return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
    }

    const emitTableUpdate = async () => {
      try {
        const res = await TableApi.getTable(props.tableId)
        if (res?.data?.data) {
          emit('updated:table', res.data.data)
        }
      } catch (e) {
        // テーブルデータ取得に失敗しても預かり金操作自体は成功しているので無視
      }
    }

    const createDeposit = async () => {
      if (!inputAmount.value || inputAmount.value <= 0) return

      isSubmitting.value = true
      try {
        const res = await DepositApi.createDeposit(props.tableId, {
          amount: inputAmount.value,
          paymentMethod: inputPaymentMethod.value,
        })
        if (res?.data?.deposit) {
          deposits.value.push(res.data.deposit)
          inputAmount.value = null
          vm.$toast.success('預かり金を登録しました')
          emit('close')
          await emitTableUpdate()
        }
      } catch (e) {
        vm.$toast.error('預かり金の登録に失敗しました')
      }
      isSubmitting.value = false
    }

    const confirmCancel = deposit => {
      cancelTarget.value = deposit
      showCancelDialog.value = true
    }

    const cancelDeposit = async () => {
      if (!cancelTarget.value) return

      cancellingId.value = cancelTarget.value.id
      try {
        await DepositApi.cancelDeposit(props.tableId, cancelTarget.value.id)
        deposits.value = deposits.value.filter(d => d.id !== cancelTarget.value.id)
        vm.$toast.success('預かり金を取消しました')
        await emitTableUpdate()
      } catch (e) {
        vm.$toast.error('預かり金の取消に失敗しました')
      }
      cancellingId.value = null
      showCancelDialog.value = false
      cancelTarget.value = null
    }

    return {
      deposits,
      inputAmount,
      inputPaymentMethod,
      isSubmitting,
      cancellingId,
      showCancelDialog,
      cancelTarget,
      totalAmount,
      formatTime,
      createDeposit,
      confirmCancel,
      cancelDeposit,
      icons: { mdiClose },
    }
  },
}
</script>
